import React, { useEffect, useMemo, useState } from 'react';

import LoaderPanel from 'Components/common/LoaderPanel';
import NoteBox from 'Components/common/NoteBox';
import VaccineMap from '@reuters-graphics/chart-module-vaccination-map/dist/index.js';
import classnames from 'classnames';
import { component } from './styles.module.scss';
import debounce from 'lodash/debounce';
import fetchJson from 'Utils/fetchJson';
import { globalTopojsonURL } from 'Constants/locations';
import useObservable from 'Components/hooks/observed';
import useWindowDimensions from 'Components/hooks/useWindowDimensions';

const globe = new VaccineMap();

const GlobeContainer = (props) => {
  const { vaccinationSummaryStatistics } = props;
  const { width: windowWidth } = useWindowDimensions();

  const chartContainer = React.createRef();

  const [topojson, setTopojson] = useState(null);
  const [chartDrawn, setChartDrawn] = useState(false);

  const isVisible = useObservable({ ref: chartContainer, rootMargin: '600px' });

  const resize = debounce(() => {
    if (!topojson) return;
    globe.draw();
  }, 250);

  useEffect(() => {
    if (topojson) return;
    // if (window.localStorage.getItem('globetopo')) {
    //   setTopojson(JSON.parse(window.localStorage.getItem('globetopo')));
    //   return;
    // }
    fetchJson(globalTopojsonURL)
      .then(topojson => {
        setTopojson(topojson);
        window.localStorage.setItem('globetopo', JSON.stringify(topojson));
      });
  }, [topojson]);

  useEffect(() => {
    if (!topojson) return;
    globe
      .selection(chartContainer.current)
      .data(vaccinationSummaryStatistics)
      .props({
        stopShow: !isVisible,
        colorScaleWidth: 140,
        globe: {
          strokeColor: "rgba(255, 255, 255, 0.5)",
          strokeWidth: 0.1,
          landFill: "rgba(153,153,153,0.15)",
          verticalAxisTilt: 8,

          colorFill: "rgb(161, 233, 99)",

          linearGradientForKey: "linear-gradient(90deg, rgba(161, 233, 99,0.05) 0%, rgba(161, 233, 99,1) 100%)",
          
          highlight: { 
            strokeColor: "rgba(255,255,255,.65)",
            strokeWidth: 1,
           },
          }
      })
      .geo(topojson)
      .draw();
      setChartDrawn(true);
    return () => {
      globe._clearTimer();
    };
  }, [topojson, chartContainer, isVisible, vaccinationSummaryStatistics]);

  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  return (
    <div
      className={classnames(component)}
    >
      <section className='wide'>
        <div className='vaccination-globe-map-container' ref={chartContainer} />
        {!chartDrawn && (
          <LoaderPanel height={windowWidth > 800 ? 866 : Math.max(windowWidth + 66, 500)}/>
        )}
      </section>
      <div className='section-spacer-internal' />
      <section>
        <NoteBox
          title='What you need to know'
          source={`Reuters has launched a global effort to track the vaccine rollout plans of over 80 countries and all 50 U.S. states. Our journalists are collecting, verifying and updating information on vaccine availability and eligibility as the plans are announced and implemented by governments and health authorities around the world.
          
Reuters is also analysing how quickly countries are vaccinating their populations, using data collected from over 130 countries by the [Our World in Data](https://ourworldindata.org/covid-vaccinations) project at the University of Oxford.
          
COVID-19 vaccination data can be reported by countries as the total number of doses administered and/or the number of people vaccinated. Because most coronavirus vaccines require two doses, many countries also report the number of people who have received just one dose and the number who have been fully vaccinated.

Many of the charts on this page show the number of people who have received *at least* one dose of a vaccine, which can offer significant protection against severe disease and reduce the risk of transmission, [according to some studies](https://www.reuters.com/article/us-health-coronavirus-vaccines-singledos/interim-uk-study-finds-one-dose-of-covid-shots-reduces-hospitalisation-idINKBN2AV14I).

If a country only reports the number of doses they have administered in total, we do not know the number of people who have been vaccinated. Those countries are excluded from our charts and statistics about people vaccinated.
`}
        />
      </section>
    </div>
  );
};

export default GlobeContainer;
